/**
 * TenantLogo
 *
 * Renders a tenant's logo at a consistent optical size across tenants.
 * Falls back to <ButterLogo variant="full" /> when the tenant handle is not
 * registered in TENANT_LOGO_MAP.
 *
 * Sizing combines two inputs:
 * - `size`  (prop)     — the size tier for the context: 'xs' (compact horizontal
 *                        bars), 'sm' (headers), 'md', or 'lg' (splash / auth
 *                        screens, the default).
 * - `logoSize` (registry) — the logo's shape: 'wide' wordmark or 'square' badge.
 *
 * Square badges are given MORE height than wide wordmarks at every tier, because
 * a wordmark and a badge sized to the same height do not read as the same size —
 * the badge looks tiny. Sizing by shape normalizes their visual weight.
 *
 * Pass `className` to override sizing entirely when a context needs exact control
 * (e.g. a 20px avatar). An explicit `className` wins over `size`.
 *
 * The fallback chain and the per-surface config merge live in `resolveBrandAsset`;
 * rendering lives in `BrandAsset`. This file is the public name and prop contract.
 *
 * @example
 * <TenantLogo tenantHandle="sweet-score" />                 // lg, shape-aware
 * <TenantLogo tenantHandle="hivegolf" size="sm" />          // compact header
 * <TenantLogo tenantHandle="linksindoor" className="size-5" /> // exact override
 */

import { BrandAsset } from './BrandAsset';
import type { BrandSurface } from './resolveBrandAsset';
import type { LogoSizeTier } from './sizing';

interface TenantLogoProps {
  /**
   * Tenant to render. Defaults to the surrounding `TenantProvider`'s handle, so
   * app code can omit it; pass explicitly to override (e.g. the Playground story).
   */
  tenantHandle?: string;
  /** Size tier for the render context. Defaults to 'lg'. Ignored when `className` is set. */
  size?: LogoSizeTier;
  /** Override sizing entirely. When omitted, size is derived from `size` + registry `logoSize`. */
  className?: string;
  ariaHidden?: boolean;
  /**
   * Override the resolved `logoViewBox` crop for this render. Mainly for the
   * Playground story — leave unset in app code so the registry value applies.
   */
  viewBox?: string;
  /** Override the resolved `roundedLogo`. Mainly for the Playground story. */
  rounded?: boolean;
  /** Override the resolved `dark.scale` multiplier. Mainly for the Playground story. */
  scale?: number;
  /**
   * The ground the logo renders on. `dark` resolves the tenant's dark-surface
   * variant from TENANT_DARK_LOGO_MAP when one exists (marks that vanish on
   * black get one; plate logos don't need one) and falls back to the regular
   * logo otherwise. It also selects the registry's `dark` presentation block and
   * flips the Butter fallback's ink. BrandCanvas passes `dark`; light app chrome
   * omits it.
   */
  surface?: BrandSurface;
}

export function TenantLogo({
  tenantHandle,
  size = 'lg',
  className,
  ariaHidden = true,
  viewBox,
  rounded,
  scale,
  surface = 'light',
}: TenantLogoProps) {
  return (
    <BrandAsset
      kind="logo"
      tenantHandle={tenantHandle}
      size={size}
      className={className}
      ariaHidden={ariaHidden}
      viewBox={viewBox}
      rounded={rounded}
      scale={scale}
      surface={surface}
    />
  );
}
